import React, { useEffect, useState, useRef } from 'react';
import { api } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/fintech/StatusPill';
import { Upload, ShieldCheck, Zap } from 'lucide-react';
import { toast } from 'sonner';

export default function KycPage() {
  const { t } = useLanguage();
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [diditStatus, setDiditStatus] = useState({ configured: false, session: null });
  const [diditBusy, setDiditBusy] = useState(false);
  const [form, setForm] = useState({ document_type: 'passport', document_number: '', issuing_country: '', address: '', city: '', postal_code: '' });
  const docRef = useRef(null);
  const selfieRef = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/kyc/status').then(r => setStatus(r.data));
    api.get('/kyc/didit/status').then(r => setDiditStatus(r.data)).catch(() => {});
  }, []);

  const startDidit = async () => {
    setDiditBusy(true);
    try {
      const r = await api.post('/kyc/didit/session');
      setDiditStatus(s => ({ ...s, session: r.data }));
      toast.info(diditStatus.configured ? t('kyc.didit_started_real') : t('kyc.didit_started_mock'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setDiditBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!docRef.current?.files?.[0]) { toast.error('Please upload document'); return; }
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
    fd.append('document', docRef.current.files[0]);
    if (selfieRef.current?.files?.[0]) fd.append('selfie', selfieRef.current.files[0]);
    try {
      await api.post('/kyc/submit', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('kyc.submitted'));
      const r = await api.get('/kyc/status');
      setStatus(r.data);
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const kycStatus = user?.kyc_status || 'NOT_STARTED';
  const canResubmit = kycStatus === 'NOT_STARTED' || kycStatus === 'REJECTED';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">{t('kyc.title')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{t('kyc.subtitle')}</p>
        </div>
        <div data-testid="kyc-status"><StatusPill status={kycStatus} /></div>
      </div>

      {kycStatus === 'APPROVED' && (
        <Card className="bg-[hsl(152_45%_94%)] border-[hsl(152_35%_80%)]"><CardContent className="p-4 flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-[hsl(152_45%_24%)]" /><div className="text-sm text-[hsl(152_45%_24%)]">{t('wallet.kyc_approved')}</div></CardContent></Card>
      )}
      {kycStatus === 'IN_REVIEW' && (
        <Card className="bg-[hsl(188_55%_94%)] border-[hsl(188_40%_82%)]"><CardContent className="p-4 text-sm text-[hsl(188_55%_26%)]">{t('wallet.kyc_pending')}</CardContent></Card>
      )}
      {kycStatus === 'REJECTED' && (
        <Card className="bg-[hsl(0_72%_95%)] border-[hsl(0_60%_85%)]"><CardContent className="p-4 text-sm text-[hsl(0_72%_35%)]">{t('wallet.kyc_rejected')} {status?.session?.reviewer_notes && <div className="mt-1 font-medium">Reason: {status.session.reviewer_notes}</div>}</CardContent></Card>
      )}

      {canResubmit && (
        <Card className="bg-white border-[hsl(var(--brand-gold))]/40" data-testid="kyc-didit-panel">
          <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-[hsl(var(--brand-navy))]/10 text-[hsl(var(--brand-navy))] flex items-center justify-center">
                <Zap className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-serif text-base text-[hsl(var(--brand-navy-2))]">{t('kyc.didit_title')}</div>
                  <span
                    data-testid="kyc-didit-mode-badge"
                    className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${diditStatus.configured ? 'bg-[hsl(152_45%_94%)] text-[hsl(152_45%_24%)] border-[hsl(152_35%_80%)]' : 'bg-[hsl(43_74%_94%)] text-[hsl(43_74%_22%)] border-[hsl(43_60%_80%)]'}`}
                  >
                    {diditStatus.configured ? t('kyc.didit_mode_live') : t('kyc.didit_mode_mock')}
                  </span>
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 max-w-lg">{t('kyc.didit_subtitle')}</p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={startDidit}
              disabled={diditBusy}
              data-testid="kyc-didit-start-button"
              className="shrink-0"
            >
              {t('kyc.didit_start')}
            </Button>
          </CardContent>
        </Card>
      )}

      {canResubmit && (
        <Card className="bg-white"><CardContent className="p-6">
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>{t('kyc.doc_type')}</Label><Select value={form.document_type} onValueChange={v => setForm({ ...form, document_type: v })}><SelectTrigger data-testid="kyc-doc-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="passport">{t('kyc.passport')}</SelectItem><SelectItem value="id_card">{t('kyc.id_card')}</SelectItem><SelectItem value="driver_license">{t('kyc.driver_license')}</SelectItem></SelectContent></Select></div>
              <div><Label>{t('kyc.doc_number')}</Label><Input data-testid="kyc-doc-number" value={form.document_number} onChange={e => setForm({ ...form, document_number: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>{t('kyc.issuing_country')}</Label><Input value={form.issuing_country} onChange={e => setForm({ ...form, issuing_country: e.target.value })} /></div>
              <div><Label>{t('kyc.address')}</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>{t('kyc.city')}</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>{t('kyc.postal_code')}</Label><Input value={form.postal_code} onChange={e => setForm({ ...form, postal_code: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('kyc.upload_doc')} *</Label>
                <div className="mt-1 rounded-xl border border-dashed bg-white p-4 text-sm text-[hsl(var(--muted-foreground))]" data-testid="kyc-document-dropzone">
                  <input ref={docRef} type="file" accept=".jpg,.jpeg,.png,.pdf" data-testid="kyc-document-file-input" className="w-full text-xs" />
                  <p className="mt-2 text-xs">JPG, PNG, PDF up to 10 MB</p>
                </div>
              </div>
              <div>
                <Label>{t('kyc.upload_selfie')}</Label>
                <div className="mt-1 rounded-xl border border-dashed bg-white p-4 text-sm text-[hsl(var(--muted-foreground))]">
                  <input ref={selfieRef} type="file" accept="image/*" data-testid="kyc-selfie-file-input" className="w-full text-xs" />
                  <p className="mt-2 text-xs">JPG, PNG up to 10 MB</p>
                </div>
              </div>
            </div>
            <Button type="submit" disabled={busy} data-testid="kyc-submit-button" className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]"><Upload className="h-4 w-4 mr-2" />{t('kyc.submit')}</Button>
          </form>
        </CardContent></Card>
      )}
    </div>
  );
}
